import _extends from "@babel/runtime/helpers/esm/extends";
export const isVariantPalette = colorPalette => colorPalette && typeof colorPalette === 'object' && Object.keys(colorPalette).some(value => {
  var _value$match;

  return (_value$match = value.match) == null ? void 0 : _value$match.call(value, /^(plain(Hover|Active|Disabled)?(Color|Bg)|outlined(Hover|Active|Disabled)?(Color|Border|Bg)|soft(Hover|Active|Disabled)?(Color|Bg)|solid(Hover|Active|Disabled)?(Color|Bg))$/);
});

const assignCss = (target, variantVar, value) => {
  if (variantVar.includes('Color')) {
    target.color = value;
  }

  if (variantVar.includes('Bg')) {
    target.backgroundColor = value;
  }

  if (variantVar.includes('Border')) {
    target.borderColor = value;
  }
};

const createPrefixVar = prefix => {
  return cssVar => `--${prefix ? `${prefix}-` : ''}${cssVar.replace(/^--/, '')}`;
};
/**
 *
 * @param name variant name
 * @example 'plain'
 *
 * @param palette object that contains palette tokens
 * @example { primary: { plainColor: '', plainHoverColor: '', ...tokens }, ...other palete }
 *
 * @param getCssVar a function that receive variant token and return a CSS variable
 *
 * result will be the stylesheet based on the palette tokens
 * @example {
 *   color: '--token',
 *   backgroundColor: '--token',
 *   '--variant-borderWidth': '0px',
 * }
 * @example {
 *   cursor: 'pointer',
 *   color: '--token',
 *   backgroundColor: '--token',
 *   '--variant-borderWidth': '1px',
 * }
 * @example {
 *   pointerEvents: 'none',
 *   cursor: 'default',
 *   color: '--token',
 *   backgroundColor: '--token',
 *   '--variant-borderWidth': '0px',
 * }
 */


export const createVariantStyle = (name, palette, getCssVar) => {
  const result = {};
  Object.entries(palette || {}).forEach(([variantVar, value]) => {
    if (variantVar.match(new RegExp(`${name}(color|bg|border)`, 'i')) && !!value) {
      const cssVar = getCssVar ? getCssVar(variantVar) : value;

      if (variantVar.includes('Hover')) {
        result.cursor = 'pointer';
      }

      if (variantVar.includes('Disabled')) {
        result.pointerEvents = 'none';
        result.cursor = 'default';
      }

      if (variantVar.match(/(Hover|Active|Disabled)/)) {
        assignCss(result, variantVar, cssVar);
      } else {
        // initial state
        if (!result['--variant-borderWidth']) {
          result['--variant-borderWidth'] = '0px';
        }

        if (variantVar.includes('Border')) {
          result['--variant-borderWidth'] = '1px';
          result.border = 'var(--variant-borderWidth) solid';
        } // border color should come later


        assignCss(result, variantVar, cssVar);
      }
    }
  });
  return result;
};
export const createTextOverrides = theme => {
  const {
    prefix,
    getCssVar
  } = theme;
  const prefixVar = createPrefixVar(prefix);
  let result = {};
  Object.entries(theme.palette).forEach(entry => {
    const [color, colorPalette] = entry;

    if (isVariantPalette(colorPalette)) {
      result = _extends({}, result, {
        [color]: {
          [prefixVar('--palette-text-primary')]: getCssVar(`palette-${color}-overrideTextPrimary`),
          [prefixVar('--palette-text-secondary')]: getCssVar(`palette-${color}-overrideTextSecondary`),
          [prefixVar('--palette-text-tertiary')]: getCssVar(`palette-${color}-overrideTextTertiary`)
        }
      });
    }
  });
  return result;
};
export const createContainedOverrides = theme => {
  const {
    prefix,
    getCssVar
  } = theme;
  const prefixVar = createPrefixVar(prefix);
  let result = {};
  Object.entries(theme.palette).forEach(entry => {
    const [color, colorPalette] = entry;

    if (isVariantPalette(colorPalette)) {
      result = _extends({}, result, {
        [color]: {
          [prefixVar('--palette-text-primary')]: '#fff',
          [prefixVar('--palette-text-secondary')]: getCssVar(`palette-${color}-100`),
          [prefixVar('--palette-text-tertiary')]: getCssVar(`palette-${color}-200`),
          '--variant-focusVisible': `rgba(255 255 255 / 0.32)`,
          '--variant-plainColor': getCssVar(`palette-${color}-100`),
          '--variant-plainHoverColor': `#fff`,
          '--variant-plainHoverBg': `rgba(255 255 255 / 0.12)`,
          '--variant-plainActiveBg': `rgba(255 255 255 / 0.2)`,
          '--variant-plainDisabledColor': getCssVar(`palette-${color}-300`),
          '--variant-outlinedColor': getCssVar(`palette-${color}-100`),
          '--variant-outlinedBorder': getCssVar(`palette-${color}-300`),
          '--variant-outlinedHoverColor': `#fff`,
          '--variant-outlinedHoverBorder': getCssVar(`palette-${color}-200`),
          '--variant-outlinedHoverBg': `rgba(255 255 255 / 0.12)`,
          '--variant-outlinedActiveBg': `rgba(255 255 255 / 0.2)`,
          '--variant-outlinedDisabledColor': getCssVar(`palette-${color}-300`),
          '--variant-outlinedDisabledBorder': `rgba(255 255 255 / 0.2)`,
          '--variant-softColor': '#fff',
          '--variant-softBg': `rgba(255 255 255 / 0.12)`,
          '--variant-softHoverBg': `rgba(255 255 255 / 0.2)`,
          '--variant-softActiveBg': `rgba(255 255 255 / 0.08)`,
          '--variant-softDisabledColor': getCssVar(`palette-${color}-300`),
          '--variant-softDisabledBg': `rgba(255 255 255 / 0.08)`,
          '--variant-solidBg': getCssVar(`palette-${color}-700`, 'rgba(0 0 0 / 0.16)'),
          '--variant-solidHoverBg': 'rgba(0 0 0 / 0.32)',
          '--variant-solidActiveBg': 'rgba(0 0 0 / 0.48)',
          '--variant-solidDisabledColor': getCssVar(`palette-${color}-300`),
          '--variant-solidDisabledBg': `rgba(255 255 255 / 0.08)`
        }
      });
    }
  });
  return result;
};
export const createVariant = (variant, theme) => {
  let result = {};

  if (theme) {
    const {
      getCssVar,
      palette
    } = theme;
    Object.entries(palette).forEach(entry => {
      const [color, colorPalette] = entry;

      if (isVariantPalette(colorPalette) && typeof colorPalette === 'object') {
        result = _extends({}, result, {
          [color]: createVariantStyle(variant, colorPalette, variantVar => getCssVar(`palette-${color}-${variantVar}`))
        });
      }
    });
  }

  result.context = createVariantStyle(variant, {
    plainColor: 'var(--variant-plainColor)',
    plainHoverColor: `var(--variant-plainHoverColor)`,
    plainHoverBg: 'var(--variant-plainHoverBg)',
    plainActiveBg: 'var(--variant-plainActiveBg)',
    plainDisabledColor: 'var(--variant-plainDisabledColor)',
    outlinedColor: 'var(--variant-outlinedColor)',
    outlinedBorder: 'var(--variant-outlinedBorder)',
    outlinedHoverColor: `var(--variant-outlinedHoverColor)`,
    outlinedHoverBorder: `var(--variant-outlinedHoverBorder)`,
    outlinedHoverBg: `var(--variant-outlinedHoverBg)`,
    outlinedActiveBg: `var(--variant-outlinedActiveBg)`,
    outlinedDisabledColor: `var(--variant-outlinedDisabledColor)`,
    outlinedDisabledBorder: `var(--variant-outlinedDisabledBorder)`,
    softColor: 'var(--variant-softColor)',
    softBg: 'var(--variant-softBg)',
    softHoverBg: 'var(--variant-softHoverBg)',
    softActiveBg: 'var(--variant-softActiveBg)',
    softDisabledColor: 'var(--variant-softDisabledColor)',
    softDisabledBg: 'var(--variant-softDisabledBg)',
    solidBg: 'var(--variant-solidBg)',
    solidHoverBg: 'var(--variant-solidHoverBg)',
    solidActiveBg: 'var(--variant-solidActiveBg)',
    solidDisabledColor: 'var(--variant-solidDisabledColor)',
    solidDisabledBg: 'var(--variant-solidDisabledBg)'
  });
  return result;
};